import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import GShell from './GShell.jsx'
import CLUB from '../../config/club.js'
import { PbccSectionTitle } from '../../components/PbccLayout.jsx'
import { watchMembers, createMember } from '../../lib/members.js'

const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'
const ACCENT = CLUB.accentColor || 'var(--ge-primary)'

// Members list — the entry point to the member backbone. Search, add, and
// open a member to their Member 360 profile. Every future suite record hangs
// off the member id linked from each row.
export default function GMembers() {
  const [raw, setRaw] = useState({})
  const [q, setQ] = useState('')

  useEffect(() => { const off = watchMembers(setRaw); return () => off() }, [])

  const members = useMemo(() =>
    Object.values(raw || {})
      .filter(Boolean)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
  [raw])

  const list = useMemo(() => {
    const s = q.trim().toLowerCase()
    if (!s) return members
    return members.filter((m) =>
      `${m.name || ''} ${m.memberNumber || ''} ${m.email || ''} ${m.phone || ''}`.toLowerCase().includes(s))
  }, [members, q])

  return (
    <GShell>
      <section>
        <PbccSectionTitle title="Members" subtitle="The account backbone every Pro Shop module references." />
        <div className="mt-3"><NewMemberForm /></div>
      </section>

      <section className="pb-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <input
            className="input w-full max-w-sm"
            placeholder="Search name, member #, email, phone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <span className="text-xs text-gray-500 shrink-0">{list.length} of {members.length}</span>
        </div>

        {list.length === 0 ? (
          <p className="text-sm text-gray-500">{members.length === 0 ? 'No members yet — add one above.' : 'No members match that search.'}</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-2">
            {list.map((m) => (
              <Link key={m.id} to={`/admin/gg/members/${m.id}`}
                className="rounded-2xl p-3 flex items-center justify-between gap-3 transition"
                style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}44`, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <div className="min-w-0">
                  <div className="font-extrabold truncate" style={{ color: PRIMARY }}>
                    {m.name}
                    {m.active === false ? <span className="ml-2 text-[10px] uppercase tracking-widest text-gray-400">(inactive)</span> : null}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {[m.memberNumber && `#${m.memberNumber}`, m.phone, m.email].filter(Boolean).join(' · ') || 'no contact on file'}
                  </div>
                </div>
                <span className="text-xs font-bold shrink-0" style={{ color: ACCENT }}>Open →</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </GShell>
  )
}

function NewMemberForm() {
  const [f, setF] = useState({ name: '', memberNumber: '', phone: '', email: '', favoriteBrands: '', notes: '' })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  const submit = async () => {
    setBusy(true); setErr('')
    try { await createMember(f); setF({ name: '', memberNumber: '', phone: '', email: '', favoriteBrands: '', notes: '' }) }
    catch (e) { setErr(e?.message || 'Could not add member.') }
    finally { setBusy(false) }
  }
  return (
    <div className="rounded-2xl p-4" style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}44`, boxShadow: '0 6px 16px rgba(0,0,0,0.06)' }}>
      <div className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: ACCENT }}>Add Member</div>
      <div className="grid sm:grid-cols-2 gap-2">
        <input className="input" placeholder="Full name" value={f.name} onChange={set('name')} />
        <input className="input" placeholder="Member # (optional)" value={f.memberNumber} onChange={set('memberNumber')} />
        <input className="input" placeholder="Phone" value={f.phone} onChange={set('phone')} />
        <input className="input" placeholder="Email" value={f.email} onChange={set('email')} />
        <input className="input" placeholder="Favorite brands (e.g. Titleist, FootJoy)" value={f.favoriteBrands} onChange={set('favoriteBrands')} />
        <input className="input" placeholder="Notes (optional)" value={f.notes} onChange={set('notes')} />
      </div>
      <button type="button" className="btn-primary text-sm mt-2" onClick={submit} disabled={busy || !f.name.trim()}>
        {busy ? 'Adding…' : 'Add Member'}
      </button>
      {err ? <p className="text-xs font-bold mt-1" style={{ color: '#B91C1C' }}>{err}</p> : null}
    </div>
  )
}
